import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Select } from '@keobook/ui';
import { useAuth } from '../lib/auth';
import { Layout } from '../components/Layout';
import { ApiError, api, type CollaboratorsResponse } from '../lib/api';
import {
  finishTotpEnrollment,
  startTotpEnrollment,
  totpFactors,
  unenrollTotp,
  type EnrolledFactor,
  type TotpEnrollment,
} from '../lib/mfa';

type EnrollState =
  | { phase: 'idle' }
  | { phase: 'starting' }
  | { phase: 'verifying'; data: TotpEnrollment };

export function Settings() {
  const { user, emailVerified, resendVerification, signOut, refresh, currentClient } = useAuth();
  // GuidedRoute redirects un-enrolled users here with state.mfaRequired so
  // we can show a "you have to enroll to proceed" panel. Without the
  // banner the user lands on Settings cold and has no idea why.
  const location = useLocation();
  const mfaRequiredFromGuidedRoute = Boolean(
    (location.state as { mfaRequired?: boolean } | null)?.mfaRequired,
  );

  const [factors, setFactors] = useState<EnrolledFactor[]>(() => totpFactors());
  const [enroll, setEnroll] = useState<EnrollState>({ phase: 'idle' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set right after a staff user successfully enrolls TOTP. The existing
  // JWT still says mfa=false; only the next signIn mints one with
  // mfa=true (off the now-stamped mfa_enrolled_at column). We surface a
  // distinct "you're staff — re-sign-in to unlock /admin" panel so they
  // don't bounce off the Admin block-state again.
  const [staffJustEnrolled, setStaffJustEnrolled] = useState(false);
  const [resent, setResent] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<{
    status: string;
    tier: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
  } | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const primaryFactor = factors[0];

  function refreshFactors() {
    setFactors(totpFactors());
  }

  useEffect(() => {
    refreshFactors();
  }, []);

  async function beginEnroll() {
    setError(null);
    setNotice(null);
    setEnroll({ phase: 'starting' });
    try {
      const data = await startTotpEnrollment(user?.email ?? 'KEOBOOKS account');
      setEnroll({ phase: 'verifying', data });
    } catch (err: unknown) {
      setError(messageFor(err));
      setEnroll({ phase: 'idle' });
    }
  }

  async function confirmEnroll(e: FormEvent) {
    e.preventDefault();
    if (enroll.phase !== 'verifying') return;
    setBusy(true);
    setError(null);
    try {
      await finishTotpEnrollment(enroll.data.secret, code);
      // Tell our API that Firebase just enrolled — stamps mfa_enrolled_at
      // on the user row so /v1/me + the admin gate stop treating the user
      // as un-enrolled. Without this round-trip, the Admin block-state
      // fires indefinitely even after a 2FA-confirmed sign-in (the column
      // is the truth source on /v1/me). Best-effort: if it fails the
      // toast surfaces it but the Firebase factor itself is enrolled, so
      // the next sign-in with 2FA still works via the JWT secondFactor
      // claim path.
      try {
        await api.notifyMfaEnrolled();
        // Refresh the AuthProvider's cached /v1/me so user.mfaEnrolled
        // flips to true. Without this the GuidedRoute keeps redirecting
        // a freshly-enrolled customer back to /settings (the mandatory-
        // TOTP gate fires off the stale state).
        await refresh();
      } catch (notifyErr) {
        // Don't block the success state on this — log + carry on.
        console.warn('notifyMfaEnrolled failed', notifyErr);
      }
      setEnroll({ phase: 'idle' });
      setCode('');
      if (user?.kind === 'staff') {
        // Staff bonus message — they came here from the Admin block-state.
        setStaffJustEnrolled(true);
        setNotice(null);
      } else {
        setNotice('Authenticator app enabled. You will be asked for a code at your next sign-in.');
      }
      refreshFactors();
    } catch (err: unknown) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function removeFactor(uid: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await unenrollTotp(uid);
      // Mirror of notifyMfaEnrolled — clear mfa_enrolled_at so the gate
      // closes again. See routes/me.ts /v1/me/mfa/cleared.
      try {
        await api.notifyMfaCleared();
      } catch (notifyErr) {
        console.warn('notifyMfaCleared failed', notifyErr);
      }
      setNotice('Authenticator app removed.');
      refreshFactors();
    } catch (err: unknown) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    try {
      await resendVerification();
      setResent(true);
    } catch {
      setResent(true);
    }
  }

  async function onOpenBillingPortal() {
    setBillingError(null);
    setBillingBusy(true);
    try {
      const { url } = await api.openBillingPortal();
      window.location.assign(url);
    } catch (err: unknown) {
      setBillingError(
        err instanceof Error
          ? err.message
          : 'Could not open the billing page. Try again, or email hello@keobooks.com.',
      );
      setBillingBusy(false);
    }
  }

  // Load the current subscription so the billing card can show a scheduled
  // cancellation + a Resume control. Best-effort — the "Manage billing" portal
  // stays available even if this read fails.
  useEffect(() => {
    let alive = true;
    api
      .getSubscription()
      .then((r) => {
        if (alive) setSubscription(r.subscription);
      })
      .catch(() => {
        /* non-fatal — leave the card in its default state */
      });
    return () => {
      alive = false;
    };
  }, [currentClient?.id]);

  async function onCancelSubscription() {
    setBillingError(null);
    setCancelBusy(true);
    try {
      const r = await api.cancelSubscription();
      setSubscription((s) =>
        s ? { ...s, cancelAtPeriodEnd: true, currentPeriodEnd: r.currentPeriodEnd } : s,
      );
      setConfirmingCancel(false);
    } catch (err: unknown) {
      setBillingError(
        err instanceof Error ? err.message : 'Could not cancel your subscription. Please try again.',
      );
    } finally {
      setCancelBusy(false);
    }
  }

  async function onResumeSubscription() {
    setBillingError(null);
    setCancelBusy(true);
    try {
      await api.resumeSubscription();
      setSubscription((s) => (s ? { ...s, cancelAtPeriodEnd: false } : s));
    } catch (err: unknown) {
      setBillingError(
        err instanceof Error ? err.message : 'Could not resume your subscription. Please try again.',
      );
    } finally {
      setCancelBusy(false);
    }
  }

  const periodEndLabel = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 1.5rem',
          }}
        >
          Settings
        </h1>

        {mfaRequiredFromGuidedRoute && !user?.mfaEnrolled && (
          <div
            role="alert"
            style={{
              marginBottom: '1.25rem',
              padding: 14,
              borderRadius: 8,
              background: 'var(--kb-amber-50, #fff7ed)',
              border: '1px solid var(--kb-amber-600, #ea580c)',
              color: 'var(--kb-gray-900)',
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>
              Two-factor authentication required
            </strong>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
              Every KEOBOOKS account uses a 6-digit authenticator code at sign-in. Set yours up
              below to continue to onboarding.
            </p>
          </div>
        )}

        {/* Account */}
        <Card>
          <CardTitle>Account</CardTitle>
          <dl className="kb-deflist">
            <div>
              <dt>Name</dt>
              <dd>
                <NameEditor
                  current={user?.fullName ?? null}
                  onSaved={refresh}
                  save={(v) => api.updateProfile(v)}
                  addLabel="Add name"
                  placeholder="Your name"
                  ariaLabel="Your name"
                />
              </dd>
            </div>
            {currentClient && (
              <div>
                <dt>Business</dt>
                <dd>
                  <NameEditor
                    current={currentClient.displayName}
                    onSaved={refresh}
                    save={(v) => api.updateBusinessName(v)}
                    addLabel="Set business name"
                    placeholder="Business name"
                    ariaLabel="Business name"
                    editable={currentClient.role === 'owner'}
                  />
                </dd>
              </div>
            )}
            <div>
              <dt>Email</dt>
              <dd>
                {user?.email ?? '—'}{' '}
                {emailVerified ? (
                  <span className="kb-pill kb-pill--ok">Verified</span>
                ) : (
                  <span className="kb-pill kb-pill--warn">Unverified</span>
                )}
              </dd>
            </div>
          </dl>
          {!emailVerified &&
            (resent ? (
              <p style={{ color: 'var(--kb-gray-600)', fontSize: 13, margin: '0.5rem 0 0' }}>
                Verification email sent. Check your inbox and spam folder.
              </p>
            ) : (
              <Button variant="ghost" size="sm" onClick={onResend}>
                Resend verification email
              </Button>
            ))}
        </Card>

        {/* Billing */}
        <div style={{ marginTop: '1.25rem' }}>
          <Card>
            <CardTitle>Billing</CardTitle>
            <CardSubtitle>
              View invoices, change plan, update payment method, or cancel — all through the secure
              Stripe customer portal.
            </CardSubtitle>
            {billingError && (
              <div role="alert" className="kb-form-error">
                {billingError}
              </div>
            )}

            {subscription?.cancelAtPeriodEnd ? (
              // Cancellation already scheduled — show when + offer to resume.
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderRadius: 8,
                  background: 'var(--kb-amber-50, #fdf6e3)',
                  border: '1px solid var(--kb-amber-200, #f0e0b0)',
                }}
              >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--kb-gray-800, #3a382f)' }}>
                  Your subscription is set to cancel
                  {periodEndLabel ? ` on ${periodEndLabel}` : ' at the end of the current period'}.
                  You keep full access until then.
                </p>
                <div style={{ marginTop: '0.75rem' }}>
                  <Button disabled={cancelBusy} onClick={onResumeSubscription}>
                    {cancelBusy ? 'Resuming…' : 'Resume subscription'}
                  </Button>
                </div>
              </div>
            ) : subscription && subscription.status !== 'canceled' && confirmingCancel ? (
              // Confirmation step for a destructive action.
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ margin: '0 0 0.75rem', fontSize: 14, color: 'var(--kb-gray-800)' }}>
                  Cancel your subscription? You&rsquo;ll keep access until the end of your current
                  billing period{periodEndLabel ? ` (${periodEndLabel})` : ''}, then it will lapse.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="danger" disabled={cancelBusy} onClick={onCancelSubscription}>
                    {cancelBusy ? 'Cancelling…' : 'Cancel at period end'}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={cancelBusy}
                    onClick={() => setConfirmingCancel(false)}
                  >
                    Keep my plan
                  </Button>
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: '0.75rem', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button disabled={billingBusy} onClick={onOpenBillingPortal}>
                {billingBusy ? 'Opening…' : 'Manage billing →'}
              </Button>
              {subscription &&
                !subscription.cancelAtPeriodEnd &&
                subscription.status !== 'canceled' &&
                !confirmingCancel && (
                  <Button variant="ghost" onClick={() => setConfirmingCancel(true)}>
                    Cancel subscription
                  </Button>
                )}
            </div>
          </Card>
        </div>

        {/* Security / 2FA */}
        <div style={{ marginTop: '1.25rem' }}>
          <Card>
            <CardTitle>Two-factor authentication</CardTitle>
            <CardSubtitle>
              Protect your account with a time-based code from an authenticator app (Google
              Authenticator, 1Password, Authy). Required at every sign-in once enabled.
            </CardSubtitle>

            {error && (
              <div role="alert" className="kb-form-error">
                {error}
              </div>
            )}
            {notice && (
              <div className="kb-form-notice" role="status">
                {notice}
              </div>
            )}

            {staffJustEnrolled && (
              <div
                role="status"
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: 'var(--kb-green-50, #e1f5ee)',
                  border: '1px solid var(--kb-green-600, #0f6e56)',
                }}
              >
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  Two-factor enrolled. One step to unlock /admin:
                </strong>
                <p style={{ margin: '0 0 8px', fontSize: 13 }}>
                  Your current session was issued before TOTP was set up, so it still says
                  <code> mfa=false</code>. Sign out and back in to mint a fresh token — the admin
                  tabs unlock immediately after that.
                </p>
                <Button
                  size="sm"
                  onClick={async () => {
                    await signOut();
                  }}
                >
                  Sign out now
                </Button>
              </div>
            )}

            {primaryFactor ? (
              <div className="kb-mfa-status">
                <div>
                  <span className="kb-pill kb-pill--ok">On</span>
                  <span style={{ marginLeft: 8, color: 'var(--kb-gray-600)', fontSize: 13 }}>
                    Authenticator app enrolled
                    {primaryFactor.enrollmentTime
                      ? ` · ${new Date(primaryFactor.enrollmentTime).toLocaleDateString()}`
                      : ''}
                  </span>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() => removeFactor(primaryFactor.uid)}
                >
                  {busy ? 'Removing…' : 'Remove'}
                </Button>
              </div>
            ) : enroll.phase === 'verifying' ? (
              <form onSubmit={confirmEnroll} noValidate>
                <ol className="kb-steps">
                  <li>Open your authenticator app and scan this QR code.</li>
                  <li>Or enter the setup key manually.</li>
                  <li>Type the 6-digit code the app shows to confirm.</li>
                </ol>
                <div className="kb-qr-row">
                  <img src={enroll.data.qrDataUrl} alt="TOTP QR code" width={180} height={180} />
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--kb-gray-400)', fontWeight: 500 }}>
                      Setup key
                    </div>
                    <code className="kb-setupkey">{enroll.data.sharedKey}</code>
                  </div>
                </div>
                <Field label="6-digit code" required>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    required
                  />
                </Field>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="submit" disabled={busy || code.length < 6}>
                    {busy ? 'Verifying…' : 'Verify and enable'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setEnroll({ phase: 'idle' });
                      setCode('');
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="kb-mfa-status">
                <div>
                  <span className="kb-pill kb-pill--warn">Off</span>
                  <span style={{ marginLeft: 8, color: 'var(--kb-gray-600)', fontSize: 13 }}>
                    Your account is protected by password only
                  </span>
                </div>
                {emailVerified ? (
                  <Button size="sm" disabled={enroll.phase === 'starting'} onClick={beginEnroll}>
                    {enroll.phase === 'starting' ? 'Preparing…' : 'Set up authenticator app'}
                  </Button>
                ) : (
                  <span className="kb-muted">Verify your email first to enable 2FA</span>
                )}
              </div>
            )}
          </Card>
          <CollaboratorsPanel />

          <DunningPanel />
        </div>
      </div>
    </Layout>
  );
}

function DunningPanel() {
  const [enabled, setEnabled] = useState(false);
  const [reminderDays, setReminderDays] = useState('3, 14, 30');
  const [sendHour, setSendHour] = useState(9);
  const [lateFeeEnabled, setLateFeeEnabled] = useState(false);
  const [lateFeeKind, setLateFeeKind] = useState<'fixed' | 'percent'>('percent');
  const [lateFeeAmountDollars, setLateFeeAmountDollars] = useState('25.00');
  const [lateFeePercent, setLateFeePercent] = useState('1.00');
  const [lateFeeAfterDays, setLateFeeAfterDays] = useState(30);
  const [lateFeeMaxCount, setLateFeeMaxCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .getDunningConfig()
      .then((cfg) => {
        if (!alive) return;
        setEnabled(cfg.enabled);
        setReminderDays(cfg.reminderDays.join(', '));
        setSendHour(cfg.sendHourLocal);
        setLateFeeEnabled(cfg.lateFeeEnabled);
        setLateFeeKind(cfg.lateFeeKind);
        setLateFeeAmountDollars((cfg.lateFeeAmountCents / 100).toFixed(2));
        setLateFeePercent((cfg.lateFeePercentBps / 100).toFixed(2));
        setLateFeeAfterDays(cfg.lateFeeAfterDays);
        setLateFeeMaxCount(cfg.lateFeeMaxCount);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not load dunning settings.'),
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    // Parse the comma-separated days; reject empty / non-numeric / dupes.
    const parsed = reminderDays
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((s) => Number(s));
    if (parsed.length === 0 || parsed.some((n) => !Number.isFinite(n) || n < 0 || n > 365)) {
      setError('Reminder days must be a comma-separated list of integers 0-365.');
      return;
    }
    const dedup = Array.from(new Set(parsed)).sort((a, b) => a - b);
    const amountCents = Math.round((Number(lateFeeAmountDollars) || 0) * 100);
    const percentBps = Math.round((Number(lateFeePercent) || 0) * 100);
    setSaving(true);
    try {
      const out = await api.updateDunningConfig({
        enabled,
        reminderDays: dedup,
        sendHourLocal: sendHour,
        lateFeeEnabled,
        lateFeeKind,
        lateFeeAmountCents: amountCents,
        lateFeePercentBps: percentBps,
        lateFeeAfterDays,
        lateFeeMaxCount,
      });
      setReminderDays(out.reminderDays.join(', '));
      setMessage(
        out.enabled
          ? `Saved. Reminders fire at ${out.reminderDays.join(', ')} days overdue.${
              out.lateFeeEnabled
                ? ` Late fee: ${
                    out.lateFeeKind === 'fixed'
                      ? `$${(out.lateFeeAmountCents / 100).toFixed(2)} fixed`
                      : `${(out.lateFeePercentBps / 100).toFixed(2)}% of balance`
                  } after ${out.lateFeeAfterDays} days, max ${out.lateFeeMaxCount}× per invoice.`
                : ' Late fees stay off.'
            }`
          : 'Dunning paused. The worker will skip your tenant until you re-enable.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardTitle>Overdue invoice reminders</CardTitle>
        <CardSubtitle>Loading…</CardSubtitle>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>Overdue invoice reminders</CardTitle>
      <CardSubtitle>
        When an invoice goes past due, KEOBOOKS can email the customer at thresholds you set. Emails
        go out daily around the time below. Each invoice gets at most as many reminders as you list,
        then stops.
      </CardSubtitle>
      <form onSubmit={save} noValidate style={{ marginTop: 12, maxWidth: 560 }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 14,
            margin: '4px 0 14px',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          Email customers automatically about overdue invoices
        </label>
        <Field label="Reminder thresholds (days overdue)">
          <Input
            value={reminderDays}
            onChange={(e) => setReminderDays(e.currentTarget.value)}
            placeholder="e.g. 3, 14, 30"
          />
        </Field>
        <div style={{ maxWidth: 200 }}>
          <Field label="Send hour (local, 0–23)">
            <Input
              type="number"
              min={0}
              max={23}
              value={sendHour}
              onChange={(e) => setSendHour(Number(e.currentTarget.value) || 0)}
            />
          </Field>
        </div>

        <hr
          style={{ border: 'none', borderTop: '1px solid var(--kb-gray-200)', margin: '14px 0' }}
        />
        <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>Late fees</p>
        <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '0 0 8px' }}>
          When enabled, the dunning worker applies a late fee to invoices that hit the threshold
          below. Default OFF. Fees post Dr A/R / Cr 4930 Late Fee Income.
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 14,
            margin: '4px 0 14px',
          }}
        >
          <input
            type="checkbox"
            checked={lateFeeEnabled}
            onChange={(e) => setLateFeeEnabled(e.currentTarget.checked)}
          />
          Automatically apply a late fee to overdue invoices
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          <Field label="Fee type">
            <Select
              value={lateFeeKind}
              onChange={(e) => setLateFeeKind(e.currentTarget.value as 'fixed' | 'percent')}
            >
              <option value="percent">Percent of balance</option>
              <option value="fixed">Fixed amount</option>
            </Select>
          </Field>
          {lateFeeKind === 'fixed' ? (
            <Field label="Fee amount ($)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={lateFeeAmountDollars}
                onChange={(e) => setLateFeeAmountDollars(e.currentTarget.value)}
              />
            </Field>
          ) : (
            <Field label="Fee percent (%)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={lateFeePercent}
                onChange={(e) => setLateFeePercent(e.currentTarget.value)}
              />
            </Field>
          )}
          <Field label="Apply after (days overdue)">
            <Input
              type="number"
              min={0}
              max={365}
              value={lateFeeAfterDays}
              onChange={(e) => setLateFeeAfterDays(Number(e.currentTarget.value) || 0)}
            />
          </Field>
        </div>
        <div style={{ maxWidth: 200 }}>
          <Field label="Max fees per invoice (1 = one-time)">
            <Input
              type="number"
              min={1}
              max={10}
              value={lateFeeMaxCount}
              onChange={(e) => setLateFeeMaxCount(Number(e.currentTarget.value) || 1)}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {message && (
          <p style={{ marginTop: 10, fontSize: 13, color: 'var(--kb-brand-green-text)' }}>
            {message}
          </p>
        )}
        {error && (
          <p style={{ marginTop: 10, fontSize: 13, color: 'var(--kb-red-600, #a32d2d)' }}>
            {error}
          </p>
        )}
      </form>
    </Card>
  );
}

function messageFor(err: unknown): string {
  const code = (err as { code?: string }).code;
  switch (code) {
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
      return 'That code is incorrect or expired. Use the current code from your app.';
    case 'auth/operation-not-allowed':
      return 'Two-factor authentication is not enabled for this project yet. Contact support.';
    case 'auth/unverified-email':
      return 'Please verify your email address before enabling two-factor authentication.';
    case 'auth/requires-recent-login':
      return 'For security, please sign out and back in, then try again.';
    default:
      return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
  }
}

// --------------------------------------------------------------------------
function PencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

// Inline single-field editor. Used for the person's name (app_user.full_name)
// and the business name (client.display_name) — pass the right `save`.
function NameEditor({
  current,
  onSaved,
  save,
  addLabel = 'Add name',
  placeholder = 'Your name',
  ariaLabel = 'Name',
  editable = true,
}: {
  current: string | null;
  onSaved: () => Promise<void>;
  save: (value: string) => Promise<unknown>;
  addLabel?: string;
  placeholder?: string;
  ariaLabel?: string;
  editable?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {current ?? '—'}
        {editable && (
          <Button
            size="sm"
            variant="ghost"
            aria-label={current ? `Edit ${ariaLabel.toLowerCase()}` : addLabel}
            title={current ? `Edit ${ariaLabel.toLowerCase()}` : addLabel}
            onClick={() => {
              setValue(current ?? '');
              setErr(null);
              setEditing(true);
            }}
            style={{
              padding: '4px 6px',
              color: 'var(--kb-gray-500)',
              borderColor: 'transparent',
            }}
          >
            <PencilIcon />
          </Button>
        )}
      </span>
    );
  }

  async function onSave() {
    if (value.trim() === '') return;
    setBusy(true);
    setErr(null);
    try {
      await save(value.trim());
      await onSaved();
      setEditing(false);
    } catch {
      setErr('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Input
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        placeholder={placeholder}
        maxLength={200}
        aria-label={ariaLabel}
      />
      <Button size="sm" onClick={() => void onSave()} disabled={busy || value.trim() === ''}>
        {busy ? 'Saving…' : 'Save'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
        Cancel
      </Button>
      {err && (
        <span role="alert" className="kb-form-error">
          {err}
        </span>
      )}
    </span>
  );
}

// Team & collaborators — invite accountant / bookkeeper (owner-only)
// --------------------------------------------------------------------------
const CAPABILITY_CATALOG: Array<{ key: string; label: string }> = [
  { key: 'view_reports', label: 'View reports' },
  { key: 'view_transactions', label: 'View transactions' },
  { key: 'edit_transactions', label: 'Edit transactions' },
  { key: 'reconcile', label: 'Reconcile' },
  { key: 'close_periods', label: 'Close periods' },
  { key: 'manage_coa', label: 'Manage chart of accounts' },
  { key: 'view_invoicing', label: 'View invoicing' },
  { key: 'manage_invoicing', label: 'Manage invoicing' },
  { key: 'manage_payments', label: 'Manage payments' },
  { key: 'view_documents', label: 'View documents' },
  { key: 'upload_documents', label: 'Upload documents' },
  { key: 'use_ai_chat', label: 'Use AI chat' },
  { key: 'decide_proposals', label: 'Approve / reject AI proposals' },
  { key: 'manage_collaborators', label: 'Manage collaborators' },
  { key: 'view_billing', label: 'View billing' },
  { key: 'manage_billing', label: 'Manage billing' },
];

function CollaboratorsPanel() {
  const [data, setData] = useState<CollaboratorsResponse | null>(null);
  const [hidden, setHidden] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'accountant' | 'bookkeeper'>('accountant');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftPerms, setDraftPerms] = useState<string[]>([]);

  function beginEdit(userId: string, current: string[]) {
    setEditingId(userId);
    setDraftPerms(current);
    setNotice(null);
    setError(null);
  }
  function toggleDraft(key: string) {
    setDraftPerms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  }
  async function savePerms(userId: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.updateCollaboratorPermissions(userId, draftPerms);
      setEditingId(null);
      setNotice('Permissions updated.');
      await load();
    } catch {
      setError('Could not update permissions.');
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    try {
      setData(await api.listCollaborators());
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setHidden(true);
      else setError('Could not load collaborators.');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  if (hidden) return null;

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      await api.inviteCollaborator(email.trim(), role);
      setNotice(`Invitation sent to ${email.trim()}.`);
      setEmail('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send the invitation.');
    } finally {
      setBusy(false);
    }
  }

  async function revokeMember(userId: string) {
    setError(null);
    setNotice(null);
    try {
      await api.revokeCollaborator(userId);
      await load();
    } catch {
      setError('Could not remove that collaborator.');
    }
  }
  async function cancelInvite(id: string) {
    setError(null);
    setNotice(null);
    try {
      await api.revokeInvitation(id);
      await load();
    } catch {
      setError('Could not cancel that invitation.');
    }
  }

  const seats = data?.seats;
  return (
    <Card>
      <CardTitle>Team &amp; collaborators</CardTitle>
      <CardSubtitle>
        Invite your accountant or bookkeeper to your books.
        {seats
          ? seats.used > seats.included
            ? ` ${seats.used} seat${seats.used === 1 ? '' : 's'} used — ${seats.included} included, ${seats.used - seats.included} paid.`
            : ` ${seats.used} of ${seats.included} seat${seats.included === 1 ? '' : 's'} used.`
          : ''}
      </CardSubtitle>

      <form
        onSubmit={onInvite}
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginTop: '0.75rem',
        }}
      >
        <div style={{ flex: '1 1 220px' }}>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="accountant@firm.com"
            />
          </Field>
        </div>
        <div>
          <Field label="Role">
            <Select
              value={role}
              onChange={(e) => setRole(e.currentTarget.value as 'accountant' | 'bookkeeper')}
            >
              <option value="accountant">Accountant</option>
              <option value="bookkeeper">Bookkeeper</option>
            </Select>
          </Field>
        </div>
        <Button type="submit" disabled={busy} style={{ boxSizing: 'border-box', height: 43 }}>
          {busy ? 'Sending…' : 'Send invite'}
        </Button>
      </form>
      {error && (
        <p role="alert" className="kb-form-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" style={{ color: '#1f7a3f', marginTop: 6 }}>
          {notice}
        </p>
      )}

      {data && (data.members.length > 0 || data.pendingInvitations.length > 0) && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 14 }}>
          <tbody>
            {data.members.map((m) => (
              <Fragment key={m.userId}>
                <tr style={{ borderTop: '1px solid #eceadf' }}>
                  <td style={{ padding: '8px' }}>{m.email}</td>
                  <td style={{ padding: '8px', textTransform: 'capitalize' }}>{m.role}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {m.role !== 'owner' && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            editingId === m.userId
                              ? setEditingId(null)
                              : beginEdit(m.userId, m.permissions)
                          }
                        >
                          {editingId === m.userId ? 'Close' : 'Permissions'}
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void revokeMember(m.userId)}
                        >
                          Remove
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
                {editingId === m.userId && (
                  <tr>
                    <td colSpan={3} style={{ padding: '8px 8px 14px', background: '#faf9f5' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: 4,
                        }}
                      >
                        {CAPABILITY_CATALOG.map((c) => (
                          <label
                            key={c.key}
                            style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}
                          >
                            <input
                              type="checkbox"
                              checked={draftPerms.includes(c.key)}
                              onChange={() => toggleDraft(c.key)}
                            />
                            {c.label}
                          </label>
                        ))}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" disabled={busy} onClick={() => void savePerms(m.userId)}>
                          {busy ? 'Saving…' : 'Save permissions'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {data.pendingInvitations.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #eceadf', color: '#6e6c66' }}>
                <td style={{ padding: '8px' }}>{p.email}</td>
                <td style={{ padding: '8px', textTransform: 'capitalize' }}>{p.role} · pending</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  <Button size="sm" variant="ghost" onClick={() => void cancelInvite(p.id)}>
                    Cancel
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
